import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import { enrichPatchWithAutoSolve } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import type { WorkspaceType } from 'state/workspaces/model';
import { getWorkspaceType } from 'state/workspaces/tabs/tabs.selector';
import { getDateStringInLocalTimezone } from 'utils/core/date';

import { formEnrichmentRequested, formInputDataChanged, formInputDataReverted } from './formData.action';

export function resetDefaultDatesThunk(tabId: string, formId: string): Thunk<void> {
  return (dispatch, _, { getNow }) => {
    const todayString = getDateStringInLocalTimezone(getNow());
    dispatch(formInputDataChangedThunk(tabId, formId, 'RefreshToDefaultDates', todayString));
  };
}

export function refreshScheduleThunk(tabId: string, formId: string): Thunk<void> {
  return dispatch => {
    dispatch(formInputDataChangedThunk(tabId, formId, 'RefreshSchedule', []));
  };
}

export function formInputDataChangedThunk(
  tabId: string,
  formId: string,
  key: string,
  value: Readonly<unknown>,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getFormInputData, isPendingEnrichment } }) => {
    const state = getState();

    const workspaceType = getWorkspaceType(state, tabId) ?? 'addonManagement';
    if (!isPendingEnrichment(state, formId)) {
      const inputData = getFormInputData(state, formId);
      const patch = { ...inputData, [key]: value };
      const enhancedPatch = enrichPatchWithAutoSolve(state, workspaceType!, tabId, formId, patch);
      dispatch(formEnrichmentRequested(tabId, formId, enhancedPatch, workspaceType!));
    } else {
      dispatch(formInputDataChanged(tabId, formId, key, value, workspaceType));
    }
  };
}

export function checkFormEnrichmentRequestNeededThunk(
  tabId: string,
  formId: string,
  workspace: WorkspaceType,
  fieldsMetadata: FieldMetadataById,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getFormInputData } }) => {
    const state = getState();
    const hiddenFields = Object.keys(fieldsMetadata).filter(fieldId => {
      const metaData = fieldsMetadata[fieldId];
      return metaData.isHidden === true || metaData.isReadOnly === true || metaData.type === 'Blank';
    });
    const inputData = getFormInputData(state, formId);

    const inputFieldIds = Object.keys(inputData);

    if (inputFieldIds.length > 0) {
      const inputDataWithoutHiddenFields = Object.keys(inputData).reduce<Record<string, Readonly<unknown>>>(
        (patchedData, key) => {
          if (!hiddenFields.includes(key)) {
            patchedData[key] = inputData[key];
          }
          return patchedData;
        },
        {},
      );

      if (Object.keys(inputDataWithoutHiddenFields).length > 0) {
        const enhancedPatch = enrichPatchWithAutoSolve(state, workspace, tabId, formId, inputDataWithoutHiddenFields);
        dispatch(formEnrichmentRequested(tabId, formId, enhancedPatch, workspace));
      } else {
        dispatch(formInputDataReverted(tabId, formId));
      }
    }
  };
}
